import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgPaper } from "@intelligems/ig-theme";
import {
  AnalyticsResourceType,
  AnalyzeResourceResponseBodyType,
} from "@intelligems/ig-types";
import { CircularProgress, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { UseQueryResult } from "@tanstack/react-query";

import { useConfiguration } from "../../api/configurations/use-configuration";
import {
  getDefaultGroupGraphFilterValue,
  selectData,
} from "../../api/formatters/dashboard";
import { selectedVariations } from "../../store/slices/variations";
import { IExperienceDashboardFilters } from "../../store/types/dashboardFilters";
import { IAppState } from "../../store/types/store";
import type {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../types/analytics";
import { ExperienceFe } from "../../types/schemas/experiences";

import type {
  GraphConfig,
  GraphConfigGroup,
} from "./Graphs/Utils/graphConfigs";
import { GraphHeaderDetails } from "./Utils/GraphHeaderDetails";
import { GraphRender } from "./Utils/GraphRender";
import { GroupGraphHeader } from "./GroupGraphHeader";

interface GraphGroupWrapperProps {
  configGroup: GraphConfigGroup;
  resource?: ExperienceFe;
  resourceType: AnalyticsResourceType;
  dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType, unknown>[];
  metricFilterIndex?: number;
  firstConfigData:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[]
    | undefined;
}

const PREFIX = "GroupGraphWrapper";

const classes = {
  dragIcon: `${PREFIX}-dragIcon`,
  graphPaper: `${PREFIX}-graphPaper`,
  fontFamily: `${PREFIX}-fontFamily`,
  loadingData: `${PREFIX}-loadingData`,
  groupGraphTitle: `${PREFIX}-groupGraphTitle`,
  groupTitleFlex: `${PREFIX}-groupTitleFlex`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.dragIcon}`]: {
    color: "rgba(0,0,0,0.38)",
    transform: "rotate(90deg)",
    "&:hover, &:focus": {
      color: theme.palette.primary.dark,
      transform: "rotate(90deg) scale(1.25)",
    },
  },

  [`& .${classes.graphPaper}`]: {
    padding: "30px",
  },

  [`& .${classes.fontFamily}`]: {
    fontFamily: theme.typography.fontFamily,
  },
  [`& .${classes.loadingData}`]: {
    marginTop: "150px",
    textAlign: "center",
  },
  [`& .${classes.groupGraphTitle}`]: {
    fontSize: "15px",
  },
  [`& .${classes.groupTitleFlex}`]: {
    textAlign: "center",
    marginBottom: "20px",
  },
}));

export const GroupGraphWrapper = ({
  configGroup,
  resource,
  dashboardData,
  firstConfigData,
  resourceType,
  metricFilterIndex,
}: GraphGroupWrapperProps) => {
  const variations = useSelector(selectedVariations);
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const dashboardFilters: IExperienceDashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const [currFilter, setCurrFilter] = useImmer<string>(
    configGroup.showFilter && firstConfigData
      ? getDefaultGroupGraphFilterValue(
          firstConfigData as BarMetricData[],
          configGroup
        )
      : configGroup.groupFilterMetric === "VARIATION_ID" &&
          resourceType === "experiences"
        ? getDefaultGroupGraphFilterValue(
            firstConfigData as BarMetricData[],
            configGroup,
            variations
          )
        : ""
  );
  useEffect(() => {
    if (firstConfigData && configGroup.showFilter) {
      if (
        configGroup.groupFilterMetric === "VARIATION_ID" &&
        resourceType === "experiences"
      ) {
        setCurrFilter(
          getDefaultGroupGraphFilterValue(
            firstConfigData as BarMetricData[],
            configGroup,
            variations
          )
        );
      } else {
        setCurrFilter(
          getDefaultGroupGraphFilterValue(
            firstConfigData as BarMetricData[],
            configGroup
          )
        );
      }
    }
  }, [firstConfigData]);

  const filterList = useMemo(() => {
    if (
      configGroup.groupFilterMetric === "VARIATION_ID" &&
      resourceType === "experiences"
    ) {
      if (dashboardFilters.statSigView === "beatControl") {
        return variations
          .filter((group) => !group.isControl)
          .map((testGroup) => ({
            id: testGroup.id,
            name: testGroup.name,
          }));
      } else {
        return variations.map((testGroup) => ({
          id: testGroup.id,
          name: testGroup.name,
        }));
      }
    } else {
      return undefined;
    }
  }, [resource, dashboardFilters.statSigView]);

  return (
    <StyledGrid item xs={12}>
      <IgPaper sx={{ minHeight: "330px" }} className={classes.graphPaper}>
        {firstConfigData && (
          <GroupGraphHeader
            groupMetricData={firstConfigData}
            config={configGroup}
            filterMethod={currFilter}
            setFilterMethod={setCurrFilter}
            filterList={filterList}
          />
        )}
        <Grid
          container
          item
          rowSpacing={2}
          xs
          direction={"row"}
          justifyContent={"space-between"}
        >
          {configGroup.configs.map((singleConfig: GraphConfig) => {
            const data = selectData(
              resourceType,
              //@ts-ignore
              dashboardData,
              singleConfig,
              resource,
              storeCurrencyConfig?.value?.locale,
              metricFilterIndex,
              variations
            );
            return (
              <Grid xs={12} md={6} lg={6} item key={singleConfig.title[0]}>
                {!data ? (
                  <div className={classes.loadingData}>
                    <CircularProgress />
                  </div>
                ) : (
                  <>
                    <GraphHeaderDetails
                      config={singleConfig}
                      metricFilterIndex={metricFilterIndex}
                    />
                    <GraphRender
                      config={singleConfig}
                      resource={resource}
                      resourceType={resourceType}
                      data={data}
                      metricFilterIndex={metricFilterIndex}
                      filterMethod={currFilter}
                    />
                  </>
                )}
              </Grid>
            );
          })}
        </Grid>
      </IgPaper>
    </StyledGrid>
  );
};
